/**
 * The knowledge base: what Zoye is allowed to say it knows.
 *
 * The pipeline — chunking, embedding, pgvector, retrieval with a floor — has
 * been built and tested for a while with no way for anyone to put a document
 * in. This is that way.
 *
 * The screen is built around one warning it must never stop showing. Without an
 * OpenAI key the API indexes through `DeterministicEmbedder`, whose vectors are
 * a hash — they are the right SHAPE and carry no meaning at all. An index built
 * with it will answer questions, plausibly and wrongly. So:
 *
 *  - the placeholder state is a banner, not a footnote;
 *  - the ingest form still works, because being able to try the pipeline is
 *    useful, but it says what it is about to build;
 *  - every document row shows the model its vectors were made with, because
 *    that is what makes a later re-index a refusal rather than a silent mix.
 *
 * Search is here for the same reason it exists on the API: seeing what the
 * agent would be handed, before trusting it with a customer.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, FlaskConical, TriangleAlert, Upload } from 'lucide-react';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client';
import { useBrands } from '../api/admin';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { formatRelative } from '../lib/format';
import { extractText } from '../lib/kb/extract';

interface KbDocumentRow {
  id: string;
  title: string;
  kind: string;
  source: string | null;
  active: boolean;
  updatedAt: string;
  /** Stored once for every brand rather than for this one. */
  shared: boolean;
  _count: { embeddings: number };
}

interface DocumentsResponse {
  documents: KbDocumentRow[];
  embeddingModel: string;
  isReal: boolean;
}

interface IngestResult {
  documentId: string;
  chunks: number;
  embeddingModel: string;
  isReal: boolean;
}

interface Passage {
  chunkId: string;
  documentTitle: string;
  source: string | null;
  text: string;
  similarity: number;
  marker: string;
}

interface SearchResult {
  passages: Passage[];
  belowThreshold: boolean;
  bestSimilarity: number | null;
  embeddingModel: string;
  isReal: boolean;
}

/**
 * Matches the kinds the ingest endpoint understands; `policy` is its default.
 *
 * `internal_procedure` is not a free choice — it changes how retrieval frames
 * the passage, and the Zendesk import sets it. Offering it here would let
 * somebody mark customer-facing copy as internal, or the reverse, from a
 * dropdown with no explanation attached.
 */
const KINDS = ['policy', 'product_sheet', 'faq', 'zendesk_article'];

export function KnowledgePage() {
  const brands = useBrands();
  const [brandId, setBrandId] = useState('');

  // Default to the first brand the person can actually administer, rather than
  // the first in the list — otherwise the form is pre-set to a brand whose
  // ingest will 403.
  const administrable = useMemo(
    () => (brands.data ?? []).filter((brand) => brand.canAdminister),
    [brands.data],
  );
  const selected = brandId || administrable[0]?.id || '';

  const documents = useQuery({
    queryKey: ['kb', 'documents', selected],
    queryFn: () => apiGet<DocumentsResponse>(`/api/kb/documents?brandId=${selected}`),
    enabled: Boolean(selected),
  });

  if (brands.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  if (administrable.length === 0) {
    return (
      <div className="mx-auto max-w-[640px] px-5 py-12 text-center">
        <TriangleAlert size={26} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
        <p className="text-sm text-ink-700">You do not administer any brand.</p>
        <p className="mt-2 text-sm text-ink-400">
          Adding knowledge changes what the agent tells customers, so it needs brand_admin or
          superadmin on the brand you are adding it to.
        </p>
      </div>
    );
  }

  const isReal = documents.data?.isReal ?? false;
  const model = documents.data?.embeddingModel ?? 'unknown';

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-5">
      {documents.data && !isReal ? <PlaceholderBanner model={model} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label htmlFor="kb-brand" className="eyebrow text-ink-500">
          Brand
        </label>
        <select
          id="kb-brand"
          value={selected}
          onChange={(event) => setBrandId(event.target.value)}
          className="rounded border border-line bg-surface px-2 py-1 text-sm text-ink-900"
        >
          {administrable.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </select>
        {documents.data ? (
          <Badge tone={isReal ? 'fact' : 'stop'}>{model}</Badge>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <ZendeskImport />
        <ProductsImport
          brandId={selected}
          brandName={administrable.find((brand) => brand.id === selected)?.name ?? 'this brand'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <IngestForm brandId={selected} isReal={isReal} />
        <SearchProbe brandId={selected} />
      </div>

      <Card className="mt-4">
        <CardHeader
          title="Documents"
          meta={
            documents.data
              ? (() => {
                  const rows = documents.data.documents;
                  const sharedCount = rows.filter((row) => row.shared).length;
                  // "12 in this brand's index" would be a lie when nine of them
                  // are shared and nothing brand-specific has been loaded yet.
                  return sharedCount === 0
                    ? `${rows.length} this brand can retrieve`
                    : `${rows.length} this brand can retrieve — ${sharedCount} shared`;
                })()
              : undefined
          }
        />
        {documents.isLoading ? (
          <p className="px-4 py-8 text-center text-sm text-ink-400">Loading…</p>
        ) : (documents.data?.documents.length ?? 0) === 0 ? (
          <div className="px-4 py-10 text-center">
            <BookOpen size={26} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">Nothing indexed for this brand.</p>
            <p className="mx-auto mt-2 max-w-[520px] text-sm text-ink-400">
              With an empty index the agent's <code className="font-mono">searchKnowledge</code>{' '}
              tool returns nothing — which it is allowed to do. It will say it does not know rather
              than invent an answer.
            </p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Title</Th>
                <Th>Kind</Th>
                <Th className="text-right">Chunks</Th>
                <Th>Source</Th>
                <Th>Updated</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {documents.data?.documents.map((document) => (
                <tr key={document.id}>
                  <Td className="font-medium text-ink-900">
                    {document.title}
                    {document.shared ? (
                      <span
                        className="ml-2 align-middle text-[11px] font-normal text-ink-400"
                        title="Shared by every brand. Editing it changes what all fourteen agents quote."
                      >
                        every brand
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    <Badge>{document.kind.replace(/_/g, ' ')}</Badge>
                  </Td>
                  <Td className="tabular text-right text-ink-700">{document._count.embeddings}</Td>
                  <Td className="max-w-[240px] truncate text-xs text-ink-400">
                    {document.source ?? '—'}
                  </Td>
                  <Td className="text-xs text-ink-500">{formatRelative(document.updatedAt)}</Td>
                  <Td className="text-right">
                    <DocumentActions document={document} brandId={selected} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/**
 * Per-document controls: deactivate, re-scope, delete.
 *
 * Deactivating is offered first and deletion last, because they are not
 * equivalent. `active` is a filter the search applies, so turning it off is
 * immediate and free to undo; deleting throws away the vectors, and getting
 * them back means paying for every chunk again. The Zendesk import swept in
 * staff-admin articles — leave policy, idle-time monitoring — and those want
 * deactivating, not destroying.
 */
function DocumentActions({ document, brandId }: { document: KbDocumentRow; brandId: string }) {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
  };
  const fail = (error: unknown) =>
    setProblem(error instanceof ApiError ? error.message : 'That did not work.');

  const patch = useMutation({
    mutationFn: (body: { active?: boolean; shared?: boolean; brandId?: string }) =>
      apiPatch(`/api/kb/documents/${document.id}`, body),
    onSuccess: refresh,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => apiDelete(`/api/kb/documents/${document.id}`),
    onSuccess: refresh,
    onError: fail,
  });

  const busy = patch.isPending || remove.isPending;

  return (
    <div className="flex items-center justify-end gap-2">
      {problem ? <span className="text-2xs text-stop-700">{problem}</span> : null}

      <button
        type="button"
        disabled={busy}
        onClick={() => patch.mutate({ active: !document.active })}
        className="text-xs text-ink-400 underline hover:text-ink-900 disabled:opacity-50"
      >
        {document.active ? 'deactivate' : 'activate'}
      </button>

      <button
        type="button"
        disabled={busy || !brandId}
        onClick={() =>
          patch.mutate(document.shared ? { brandId } : { shared: true })
        }
        title={
          document.shared
            ? 'Narrow this to the selected brand only'
            : 'Share this with every brand'
        }
        className="text-xs text-ink-400 underline hover:text-ink-900 disabled:opacity-50"
      >
        {document.shared ? 'this brand only' : 'share with all'}
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={() => {
          // Named in the prompt, because "delete this document?" over a table
          // of forty rows is a question about the wrong thing.
          if (
            window.confirm(
              `Delete “${document.title}” and its ${document._count.embeddings} chunks? Deactivating is reversible; this is not.`,
            )
          ) {
            remove.mutate();
          }
        }}
        className="text-xs text-ink-400 underline hover:text-stop-700 disabled:opacity-50"
      >
        delete
      </button>
    </div>
  );
}

interface ImportResult {
  found: number;
  imported: string[];
  skipped: string[];
  failed: Array<{ title: string; reason: string }>;
}

/**
 * Pulls the support team's Zendesk help centre into the shared index.
 *
 * Re-running it is the update path rather than a duplicate: ingest upserts on
 * scope and title, so an edited article replaces its own chunks.
 */
function ZendeskImport() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ImportResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => apiPost<ImportResult>('/api/kb/import/zendesk', {}),
    onSuccess: async (data) => {
      setResult(data);
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'The import did not run.');
      setResult(null);
    },
  });

  return (
    <Card className="mb-4">
      <CardHeader title="Import from Zendesk" meta="shared across every brand" />
      <div className="space-y-3 px-4 py-3">
        <p className="text-sm text-ink-500">
          Pulls the Customer Support knowledge base. These articles are written for staff, so they
          are stored as internal notes — the agent reasons from them and never quotes them.
        </p>
        {/* The `Button` primitive, not hand-rolled classes. The first version
            used `bg-accent`, which is not a class in this theme — the scale is
            `accent-600` — so it rendered as plain text and read as a caption
            rather than the action of the card. */}
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() => run.mutate()}
          disabled={run.isPending}
        >
          <Upload size={14} strokeWidth={2} aria-hidden />
          {run.isPending ? 'Importing…' : 'Import all articles'}
        </Button>

        {problem ? <p className="text-sm text-stop-700">{problem}</p> : null}

        {result ? (
          <div className="space-y-1 text-sm text-ink-700">
            <p>
              {result.imported.length} of {result.found} indexed.
            </p>
            {result.skipped.length > 0 ? (
              // Named rather than counted: "3 skipped" reads as noise, whereas
              // seeing WHICH policy is missing is what makes somebody fix it.
              <p className="text-ink-500">
                Skipped, too little text to be useful: {result.skipped.join(', ')}.
              </p>
            ) : null}
            {result.failed.length > 0 ? (
              <p className="text-stop-700">
                Failed: {result.failed.map((f) => `${f.title} (${f.reason})`).join('; ')}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Pull the selected brand's products from the catalogue into ITS index.
 *
 * Everything passes the allowlist server-side: what a product is, what is in
 * it, the label's directions — never the marketing copy, the page's
 * guarantee banner, or prices. Re-running is the update path.
 */
function ProductsImport({ brandId, brandName }: { brandId: string; brandName: string }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ImportResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => apiPost<ImportResult>('/api/kb/import/products', { brandId }),
    onSuccess: async (data) => {
      setResult(data);
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'The import did not run.');
      setResult(null);
    },
  });

  return (
    <Card className="mb-4">
      <CardHeader title="Import products" meta={`${brandName} only`} />
      <div className="space-y-3 px-4 py-3">
        <p className="text-sm text-ink-500">
          Pulls {brandName}&rsquo;s catalogue and indexes only what an agent may stand behind:
          name, ingredients, the label&rsquo;s directions — and, for devices, specifications and
          setup steps. Marketing copy, guarantee banners and prices never enter the index —
          pricing is answered live.
        </p>
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() => run.mutate()}
          disabled={run.isPending || !brandId}
        >
          <Upload size={14} strokeWidth={2} aria-hidden />
          {run.isPending ? 'Importing…' : `Import ${brandName} products`}
        </Button>

        {problem ? <p className="text-sm text-stop-700">{problem}</p> : null}

        {result ? (
          <div className="space-y-1 text-sm text-ink-700">
            <p>
              {result.imported.length} of {result.found} indexed.
            </p>
            {result.skipped.length > 0 ? (
              <p className="text-ink-500">Skipped: {result.skipped.join(', ')}.</p>
            ) : null}
            {result.failed.length > 0 ? (
              <p className="text-stop-700">
                Failed: {result.failed.map((f) => `${f.title} (${f.reason})`).join('; ')}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function IngestForm({ brandId, isReal }: { brandId: string; isReal: boolean }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('policy');
  const [source, setSource] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // Defaults to off. Sharing writes a paragraph fourteen agents will quote to
  // customers, so it should be a thing somebody ticked, not the path of least
  // resistance.
  const [shared, setShared] = useState(false);

  const ingest = useMutation({
    mutationFn: () =>
      apiPost<IngestResult>('/api/kb/documents', {
        ...(shared ? { shared: true } : { brandId }),
        title: title.trim(),
        kind,
        body,
        source: source.trim() || null,
      }),
    onSuccess: async (data) => {
      setResult(
        data.isReal
          ? `Indexed ${data.chunks} chunk(s) with ${data.embeddingModel}.`
          : `Indexed ${data.chunks} chunk(s) with ${data.embeddingModel} — the placeholder. These vectors carry no meaning and must be rebuilt before anyone relies on them.`,
      );
      setProblem(null);
      setTitle('');
      setBody('');
      setSource('');
      await queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'That did not go in.');
      setResult(null);
    },
  });

  const ready = title.trim().length > 0 && body.trim().length > 0 && (shared || Boolean(brandId));

  const [extractWarnings, setExtractWarnings] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);

  const readFile = async (file: File) => {
    // Extraction happens in the browser and lands in the text box below, so
    // what gets indexed is exactly what the uploader can read first — a PDF
    // that extracts as rubbish is visible as rubbish, not embedded cleanly.
    setExtracting(true);
    setProblem(null);
    try {
      const extracted = await extractText(file);
      setBody(extracted.text);
      setExtractWarnings(extracted.warnings);
      if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ''));
      if (!source.trim()) setSource(file.name);
    } catch (error: unknown) {
      setProblem(error instanceof Error ? error.message : 'Could not read that file.');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Add a document" meta={isReal ? undefined : 'placeholder embedder'} />
      <div className="space-y-3 px-4 py-3">
        <label
          htmlFor="kb-shared"
          className="flex cursor-pointer items-start gap-2 rounded border border-line bg-surface-sunken px-3 py-2"
        >
          <input
            id="kb-shared"
            type="checkbox"
            checked={shared}
            onChange={(event) => setShared(event.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-ink-700">
            <span className="font-medium text-ink-900">Every brand</span>
            <span className="mt-0.5 block text-xs text-ink-400">
              {shared
                ? 'All fourteen brands will retrieve this. Superadmin only.'
                : 'Refunds, returns, shipping — the policies that are the same everywhere. Leave off for anything brand-specific.'}
            </span>
          </span>
        </label>

        <div>
          <label htmlFor="kb-title" className="eyebrow mb-1 block text-ink-500">
            Title
          </label>
          <input
            id="kb-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Refund and guarantee policy"
            className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="kb-kind" className="eyebrow mb-1 block text-ink-500">
              Kind
            </label>
            <select
              id="kb-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
            >
              {KINDS.map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="kb-source" className="eyebrow mb-1 block text-ink-500">
              Source
            </label>
            <input
              id="kb-source"
              value={source}
              onChange={(event) => setSource(event.target.value)}
              placeholder="URL or filename"
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
            />
          </div>
        </div>

        <div>
          <label htmlFor="kb-body" className="eyebrow mb-1 block text-ink-500">
            Text
          </label>
          <textarea
            id="kb-body"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={9}
            placeholder="Paste the document. Markdown headings are kept with their chunks, so a dosage never travels without its product name."
            className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-xs text-ink-900"
          />
          <div className="mt-1.5 flex items-center gap-2">
            <label className="cursor-pointer text-2xs text-accent-700 hover:underline">
              <input
                type="file"
                accept=".txt,.md,.markdown,.docx,.pdf"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void readFile(file);
                  event.target.value = '';
                }}
              />
              {extracting ? 'Extracting…' : 'Load a .md, .txt, .docx or .pdf file'}
            </label>
            <span className="text-2xs text-ink-400">
              Extraction happens in your browser — check the text before indexing it.
            </span>
          </div>
          {extractWarnings.length > 0 ? (
            <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-2xs text-hold-700">
              {extractWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <ChunkPreview body={body} />

        <Button
          variant="primary"
          size="sm"
          disabled={!ready || ingest.isPending}
          onClick={() => ingest.mutate()}
        >
          <Upload size={13} strokeWidth={1.75} aria-hidden />
          {ingest.isPending ? 'Indexing…' : 'Index document'}
        </Button>

        {result ? <p className="text-xs text-ok-700">{result}</p> : null}
        {problem ? <p className="text-xs text-stop-700">{problem}</p> : null}
      </div>
    </Card>
  );
}

/**
 * How the text will chunk, before it is stored — the server's own chunker
 * (via /api/kb/preview), so the preview cannot disagree with the ingest.
 */
function ChunkPreview({ body }: { body: string }) {
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: () =>
      apiPost<{ count: number; chunks: Array<{ index: number; text: string }> }>(
        '/api/kb/preview',
        { body },
      ),
    onSuccess: () => {
      setProblem(null);
      setOpen(true);
    },
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not preview.'),
  });

  if (!body.trim()) return null;

  return (
    <div>
      <Button size="sm" variant="ghost" disabled={preview.isPending} onClick={() => preview.mutate()}>
        {preview.isPending ? 'Chunking…' : 'Preview chunks'}
      </Button>
      {problem ? <p className="mt-1 text-2xs text-stop-700">{problem}</p> : null}

      {open && preview.data ? (
        <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto rounded border border-line bg-surface-sunken px-3 py-2">
          <p className="text-2xs text-ink-500">
            This will index as {preview.data.count} chunk{preview.data.count === 1 ? '' : 's'} —
            each is retrieved (and quoted from) independently.
          </p>
          {preview.data.chunks.map((chunk) => (
            <div key={chunk.index} className="rounded border border-line bg-surface px-2.5 py-1.5">
              <p className="text-2xs font-semibold text-ink-400">
                chunk {chunk.index + 1} of {preview.data?.count}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-2xs text-ink-700">
                {chunk.text.length > 400 ? `${chunk.text.slice(0, 400)}…` : chunk.text}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SearchProbe({ brandId }: { brandId: string }) {
  const [query, setQuery] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: () => apiPost<SearchResult>('/api/kb/search', { brandId, query: query.trim() }),
    onSuccess: () => setProblem(null),
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Search failed.'),
  });

  const result = search.data;

  return (
    <Card>
      <CardHeader title="What would the agent get?" />
      <div className="space-y-3 px-4 py-3">
        <div className="flex gap-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && query.trim()) search.mutate();
            }}
            placeholder="How many capsules a day?"
            aria-label="Search the knowledge base"
            className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
          />
          <Button size="sm" disabled={!query.trim() || search.isPending} onClick={() => search.mutate()}>
            <FlaskConical size={13} strokeWidth={1.75} aria-hidden />
            Search
          </Button>
        </div>

        {problem ? <p className="text-xs text-stop-700">{problem}</p> : null}

        {result ? (
          result.passages.length === 0 ? (
            <div className="rounded border border-line bg-surface-sunken px-3 py-2.5 text-xs text-ink-500">
              {/* The distinction that matters: "we know nothing" versus "we know
                  things and none of them are about this". Both are correct
                  answers and the agent behaves differently for each. */}
              {result.belowThreshold
                ? `Nothing above the similarity floor. The closest was ${result.bestSimilarity?.toFixed(2) ?? '—'}, so the agent would be handed nothing and would say it does not know.`
                : 'No hits at all — this brand has nothing indexed that could match.'}
            </div>
          ) : (
            <ol className="space-y-2">
              {result.passages.map((passage) => (
                <li key={passage.chunkId} className="rounded border border-line px-3 py-2">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="font-mono text-2xs text-accent-700">{passage.marker}</span>
                    <span className="text-xs font-medium text-ink-900">
                      {passage.documentTitle}
                    </span>
                    <span className="tabular ml-auto text-2xs text-ink-400">
                      {passage.similarity.toFixed(3)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-xs text-ink-700">{passage.text}</p>
                </li>
              ))}
            </ol>
          )
        ) : null}
      </div>
    </Card>
  );
}

function PlaceholderBanner({ model }: { model: string }) {
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-line bg-stop-100 px-4 py-3">
      <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0 text-stop-700" aria-hidden />
      <div className="text-xs text-stop-700">
        <span className="font-semibold">This index is not real.</span> No{' '}
        <code className="font-mono">OPENAI_API_KEY</code> is set, so documents are embedded with{' '}
        <code className="font-mono">{model}</code> — a hash of the text. The vectors are the right
        shape and carry no meaning, so search will return confident nonsense. Every document indexed
        now must be re-indexed once the key is in place; the stored model id makes that a refusal
        rather than a silent mix of two vector spaces.
      </div>
    </div>
  );
}
